/**
 * นำเข้าสต๊อกตั้งต้น (Initial Stock Import) — D-032, D-080
 *
 * สองขั้นที่ต้องมีสิทธิ์คนละตัว (คนเดียวถือทั้งสองสิทธิ์แล้วทำต่อเนื่องได้ — ข้อยกเว้นที่ผู้ใช้ตัดสิน D-178): **สร้าง + ตรวจ batch** (`INVENTORY` CREATE; ยังไม่แตะสต๊อก) แล้ว **โพสต์ทั้ง batch** (APPROVE;
 * all-or-nothing, แก้ไม่ได้หลัง POSTED) batch ที่มีบรรทัดผิดแม้ใบเดียวโพสต์ไม่ได้ — หน้าจอไม่มีปุ่มโพสต์ให้กดจน batch เป็น VALIDATED
 * ข้อมูลวางจาก Excel (TSV) หรือ CSV: ที่เก็บ, รหัสสินค้า, จำนวน, หน่วย, ต้นทุน/หน่วย, [Lot ผู้ขาย]
 */

import { useState, type FormEvent } from 'react';

import { StatusBadge } from '../StatusBadge';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createOpeningStockBatch,
  parseOpeningStockText,
  postOpeningStockBatch,
  type OpeningStockBatch,
  type OpeningStockResult,
} from '../../lib/inventory/openingStock';
import { stockFailureText } from '../../lib/inventory/stock';
import { selectablePlants } from '../productionOrders/productionOrderView';

const INVENTORY_MODULE = 'INVENTORY';

export interface OpeningStockScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

function statusTone(status: string): 'success' | 'danger' | 'info' | 'neutral' {
  if (status === 'POSTED') {
    return 'success';
  }
  if (status === 'INVALID') {
    return 'danger';
  }
  return status === 'VALIDATED' ? 'info' : 'neutral';
}

export function OpeningStockScreen({ permissions, apiBaseUrl, fetchImpl }: OpeningStockScreenProps) {
  const plants = selectablePlants(permissions);
  const canCreate = can(permissions, INVENTORY_MODULE, 'CREATE');
  const canPost = can(permissions, INVENTORY_MODULE, 'APPROVE');
  const options = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };

  const [chosenPlant, setPlant] = useState('');
  const plant = chosenPlant !== '' ? chosenPlant : plants.length === 1 ? (plants[0] ?? '') : '';
  const [text, setText] = useState('');
  const [batch, setBatch] = useState<OpeningStockBatch | null>(null);
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // แก้ข้อมูลหรือเปลี่ยนโรงงานหลังตรวจแล้ว = batch ที่ตรวจไว้ไม่ตรงกับสิ่งที่เห็นอีกต่อไป: ทิ้งทั้ง batch (และปุ่มโพสต์)
  // ไม่ให้ผู้อนุมัติโพสต์ข้อมูลที่ต่างจากในช่องกรอก
  const changed = (apply: () => void) => {
    apply();
    setBatch(null);
    setProblems([]);
    setError(null);
  };

  const accept = (result: OpeningStockResult) => {
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    setBatch(result.batch);
  };

  const validate = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBatch(null);
    if (plant === '') {
      setError('เลือกโรงงาน');
      return;
    }
    const parsed = parseOpeningStockText(text);
    setProblems(parsed.errors);
    if (parsed.errors.length > 0 || parsed.lines.length === 0) {
      if (parsed.lines.length === 0 && parsed.errors.length === 0) {
        setError('วางข้อมูลอย่างน้อยหนึ่งบรรทัด');
      }
      return;
    }
    setBusy(true);
    accept(await createOpeningStockBatch(plant, parsed.lines, options));
    setBusy(false);
  };

  const post = async () => {
    if (batch === null) {
      return;
    }
    setBusy(true);
    setError(null);
    accept(await postOpeningStockBatch(batch.batch_id, options));
    setBusy(false);
  };

  return (
    <div className="cap-page" data-testid="opening-stock">
      <div className="cap-page__head">
        <h1 className="cap-page__title">นำเข้าสต๊อกตั้งต้น</h1>
      </div>

      {!canCreate && (
        <div className="cap-notice" role="status" data-testid="opening-read-only">
          <p className="cap-notice__text">
            สิทธิ์ชุดนี้สร้าง batch ไม่ได้ — ต้องมี <code className="cap-table__code">can_create</code> ของโมดูล{' '}
            <code className="cap-table__code">{INVENTORY_MODULE}</code>
          </p>
        </div>
      )}

      {canCreate && (
        <form className="cap-form" onSubmit={(e) => void validate(e)}>
          <label className="cap-field">
            <span className="cap-field__label">โรงงาน</span>
            <select className="cap-input" aria-label="โรงงาน" value={plant} onChange={(e) => changed(() => setPlant(e.target.value))}>
              <option value="">— เลือกโรงงาน —</option>
              {plants.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
          <label className="cap-field">
            <span className="cap-field__label">
              ข้อมูล (วางจาก Excel หรือ CSV: ที่เก็บ, รหัสสินค้า, จำนวน, หน่วย, ต้นทุน/หน่วย, Lot ผู้ขาย)
            </span>
            <textarea
              className="cap-input"
              aria-label="ข้อมูลสต๊อกตั้งต้น"
              rows={8}
              value={text}
              onChange={(e) => changed(() => setText(e.target.value))}
            />
          </label>
          <div className="cap-form__actions">
            <button type="submit" className="cap-button cap-button--primary" disabled={busy}>
              {busy ? 'กำลังทำงาน…' : 'ตรวจสอบ (ยังไม่เปลี่ยนสต๊อก)'}
            </button>
          </div>
        </form>
      )}

      {problems.length > 0 && (
        <ul role="alert" data-testid="opening-parse-errors">
          {problems.map((problem) => (
            <li key={problem} className="cap-notice__note">
              {problem}
            </li>
          ))}
        </ul>
      )}
      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="opening-error">
          {error}
        </p>
      )}

      {batch !== null && (
        <section data-testid="opening-batch">
          <h2 className="cap-notice__title">
            Batch <code className="cap-table__code">{batch.batch_id.slice(0, 8)}</code> โรงงาน {batch.plant_code}{' '}
            <StatusBadge tone={statusTone(batch.status)}>{batch.status}</StatusBadge>
          </h2>
          <table className="cap-table" data-testid="opening-lines">
            <thead>
              <tr>
                <th>#</th>
                <th>ที่เก็บ</th>
                <th>สินค้า</th>
                <th style={{ textAlign: 'right' }}>จำนวน</th>
                <th>หน่วย</th>
                <th style={{ textAlign: 'right' }}>ต้นทุน/หน่วย</th>
                <th>ผลตรวจ</th>
              </tr>
            </thead>
            <tbody>
              {batch.lines.map((line) => (
                <tr key={line.line_no} data-invalid={line.validation_error !== null}>
                  <td>{line.line_no}</td>
                  <td>{line.storage_location_code ?? '—'}</td>
                  <td>{line.product_code ?? '—'}</td>
                  <td className="cap-table__num">{line.quantity}</td>
                  <td>{line.uom_code}</td>
                  <td className="cap-table__num">{line.unit_cost}</td>
                  <td>
                    {line.validation_error === null ? (
                      <StatusBadge tone="success">ผ่าน</StatusBadge>
                    ) : (
                      <code className="cap-table__code">{line.validation_error}</code>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {batch.status === 'INVALID' && (
            <p className="cap-notice__note" data-testid="opening-invalid-note">
              มีบรรทัดไม่ผ่าน — แก้ข้อมูลแล้วตรวจสอบใหม่เป็น batch ใหม่ (โพสต์บางส่วนไม่ได้)
            </p>
          )}
          {batch.status === 'VALIDATED' && canPost && (
            <button type="button" className="cap-button cap-button--primary" disabled={busy} onClick={() => void post()}>
              โพสต์ทั้ง batch (แก้ไม่ได้หลังโพสต์)
            </button>
          )}
          {batch.status === 'VALIDATED' && !canPost && (
            <p className="cap-notice__note" data-testid="opening-needs-approver">
              บัญชีนี้โพสต์ไม่ได้ — ต้องมีสิทธิ์อนุมัติ (<code className="cap-table__code">can_approve</code>) ของ INVENTORY (ผู้ที่มีสิทธิ์โพสต์ batch ที่ตัวเองสร้างได้)
            </p>
          )}
          {batch.status === 'POSTED' && (
            <p className="cap-notice__text" data-testid="opening-posted">
              โพสต์แล้ว {batch.posted_at ?? ''} — สต๊อกเข้า ledger และเอกสารบัญชี Opening Balance ถูกสร้างแล้ว
            </p>
          )}
        </section>
      )}
    </div>
  );
}
